import { useCallback, useMemo, useSyncExternalStore } from "react";
import { PrefetchManager, type PrefetchEntry } from "./PrefetchManager";

// Singleton instance — persists across renders and component mounts
const manager = new PrefetchManager(3);

// Simple subscription mechanism so React re-renders when cache changes
let version = 0;
const listeners = new Set<() => void>();

function notify() {
  version++;
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return version;
}

/**
 * Hook that provides access to the singleton PrefetchManager.
 * Triggers re-renders when prefetch state changes.
 */
export function usePrefetch() {
  // Subscribe to changes so we get re-renders
  useSyncExternalStore(subscribe, getSnapshot);

  const prefetch = useCallback(async (documentId: string, priority: PrefetchEntry["priority"] = "medium") => {
    await manager.prefetch(documentId, priority);
    notify();
  }, []);

  const getDocument = useCallback((documentId: string): PrefetchEntry | null => {
    return manager.get(documentId);
  }, []);

  const isReady = useCallback((documentId: string): boolean => {
    return manager.isReady(documentId);
  }, []);

  const evict = useCallback((documentId: string) => {
    manager.evict(documentId);
    notify();
  }, []);

  const clear = useCallback(() => {
    manager.clear();
    notify();
  }, []);

  const memoryUsage = useMemo(() => manager.getMemoryUsage(), []);

  return { prefetch, getDocument, isReady, evict, clear, memoryUsage };
}

/** Direct access for non-React code (e.g., WebSocket handler). */
export { manager as prefetchManager };
